// ═══════════════════════════════════════════════════════════════════════════════════════════
// SendSearchPanel — the body of חיפוש סוכנים ומפקחים, with NO page chrome.
//
// Extracted from SendSearchScreen.tsx so the same code can render in two places:
//   • `SendSearchScreen.tsx`            — the standalone /SendSearch route (DefaultScreen + gate)
//   • `DataSources.tsx` tab "sendsearch" — the LAST tab. The strip is built from a permission-gated
//     registry there, so it is 1–3 tabs wide, not a fixed three:
//     מקורות | קמפיינים לשליחה ממקור | חיפוש סוכנים ומפקחים
// Neither copy may fork: a filter that behaves differently depending on how the user got here is
// exactly the invisible inconsistency this screen exists to expose in the SEND data.
// The two hosts do NOT share a title string, and that is deliberate: the standalone route is named
// "דוח מקורות" (a product name), the tab keeps the literal "חיפוש סוכנים ומפקחים" (PO decision).
//
// What this component deliberately does NOT do, because its two hosts already do it:
//   • No `DefaultScreen` wrapper — the host owns the page shell.
//   • No feature gate / Redirect — SendSearchScreen gates on DATA_SOURCES *and* !HideRecipients
//     (matching SendSearchController, which answers Search with 405 for that permission), and
//     DataSources.tsx applies BOTH tests to the tab entry in its TABS registry before rendering it.
//     Gating a third time here would be a third place to keep in sync.
//   • No <h1> unless asked (`showTitle`) — inside the tab the page title is "מקורות נתונים" and
//     the tab label already names this view, so a second heading is noise. The standalone route
//     passes showTitle so it keeps its own heading.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Tooltip, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Loader } from '../../components/Loader/Loader';
import {
    searchSends, getSendProvenance, getSendRowValues, getSendSearchFilterFields,
    searchSupervisorSends, getSupervisorAgents,
    setFilters, setPageIndex, setPageSize, clearFilters,
    pushDrawer, popDrawer, closeDrawer,
} from '../../redux/reducers/sendSearchSlice';
import {
    SS,
    SendSearchRequest,
    SendSearchRow,
    SendSearchFilters as Filters,
    SupervisorSendRow,
    DrawerEntry,
    DrawerSubtitlePart,
    eRowKind,
    eRoleFilter,
    eSendSearchChannel,
    DEFAULT_PAGE_SIZE,
    rowHasEmailAttempt,
    rowRecipientEmailBlockOf,
    rowRecipientPhoneOf,
    sendSearchRowKey,
    toSendSearchRequest,
} from '../../Models/DataSources/SendSearch';
import { eSendChannel } from '../../Models/DataSources/SmartSend';
import SendSearchFiltersBar, { CampaignOption } from './components/SendSearchFilters';
import {
    SendSearchField,
    SendSearchFilterRule,
    SendSearchSort,
    completeRules,
    defaultSendSearchSort,
    fieldByKey,
} from './components/SendSearchAdvanced';
import { PulseemFeatures } from '../../model/PulseemFields/Fields';
import SendSearchTable from './components/SendSearchTable';
import AgentDrawer from './components/AgentDrawer';
import RollupDrawer from './components/RollupDrawer';
import DrawerStack from './components/DrawerStack';
import SendSearchExportDialog from './components/SendSearchExportDialog';

// Identity of a set of applied advanced rules, for the catalog-drift check in the panel (review
// round 2): which rules — by Id — a search carries, in their order.
const rulesKeyOf = (rules: SendSearchFilterRule[]): string => rules.map((r) => r.Id).join('|');

interface Props {
    // The standalone route renders its own heading; the tab does not. Default off, so embedding
    // this panel anywhere new does not silently add a duplicate <h1>.
    showTitle?: boolean;
    // 11-sendsearch §8: the APP-WIDE sheet (`useClasses`, App.js). Only the WhatsApp phone in
    // WaSentMessageDialog reads it — `WhatsappMobilePreview` is styled by `whatsappStyles.js` +
    // `ganaralStyle.js` classes off this object, exactly as SmartSend's summary passes `appClasses`.
    // A second, locally built copy of those rules would NOT be the same styles: the app sheet is
    // created outside the theme provider and is never flipped by jss-rtl (ganaralStyle.js:2656-2659),
    // a sheet built here would be — so both hosts hand theirs down instead.
    classes?: any;
}

const SendSearchPanel: React.FC<Props> = ({ showTitle, classes }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const isRTL = useSelector((state: any) => state.core && state.core.isRTL);
    // Sub-user permissions, from the same `state.core` slice `DataSources.tsx:72` reads them from.
    const userRoles = useSelector((state: any) => state.core && state.core.userRoles);
    // ACCOUNT-level entitlements, a different axis from the sub-user permissions above. Read from
    // `state.common` — the shape `Groups.js:68` and `DynamicGroups.tsx:73` read it from — because of
    // LOCK_EXPORT_DATA; see `canExport`.
    const { accountFeatures } = useSelector((state: any) => state.common);
    const sendSearch = useSelector((state: any) => state.sendSearch);

    const filters: Filters = sendSearch.filters;
    const items: SendSearchRow[] = sendSearch.items ?? [];
    const drawerStack: DrawerEntry[] = sendSearch.drawerStack ?? [];

    // ── advanced filters + sort (CONTRACT §2) ────────────────────────────────────────────────
    // Held HERE and not in the slice, deliberately and temporarily: `sendSearchSlice.ts` and
    // `SendSearchFilters` (the state type) belong to B4, and CONTRACT §3 gives a file exactly one
    // owner. Local state keeps this screen compiling and behaving correctly on its own; when B4's
    // slice fields land, these three lines move into `useSelector` and NOTHING else in this file or
    // in the two components below changes, because the shapes are the same. Recorded in LEDGER.
    //
    // 🔴 FIXED 2026-08-08 (review R1-02). This read was `sendSearch.searchableFields`, a key the
    // reducer NEVER writes — the slice's field list is `filterFields` (sendSearchSlice.ts:88, :186,
    // :466). The selector above is `(state: any)`, so tsc could not see it; `?? []` then yielded an
    // empty array for the life of the session, `advAvailable` (SendSearchFilters.tsx:98) was
    // permanently false, "עוד מסננים" was permanently disabled, and `AdvancedFilterBuilder` was never
    // mounted at all (:302). The whole client half of CONTRACT §2 was inert on a live audit screen.
    //
    // The comment that stood here claimed the empty array was "§2's correct degrade, not a bug" —
    // it pre-labelled the broken state as intentional, which is why three reviews walked past it.
    // The premise was false: the server projects the list fine; the CLIENT was reading the wrong key.
    const searchableFields: SendSearchField[] = sendSearch.filterFields ?? [];
    const [advRules, setAdvRules] = useState<SendSearchFilterRule[]>([]);
    const [advSort, setAdvSort] = useState<SendSearchSort>(defaultSendSearchSort());

    // A REF beside the state, read by `buildRequest`. The chips fire `onRulesChange` and `onSearch`
    // in the same handler; reading `advRules` there would send the value from BEFORE the removal —
    // the grid would come back still narrowed by a rule the user just deleted, which is worse than
    // not refetching at all. The ref is written synchronously, so the request is never one render
    // behind what is on screen.
    const advRef = useRef<{ rules: SendSearchFilterRule[]; sort: SendSearchSort }>({
        rules: [], sort: defaultSendSearchSort(),
    });

    const setRules = (next: SendSearchFilterRule[]) => { advRef.current.rules = next; setAdvRules(next); };
    const setSort = (next: SendSearchSort) => { advRef.current.sort = next; setAdvSort(next); };

    // ── the APPLIED copy (11-sendsearch §8 bug fix) ──────────────────────────────────────────
    // `advRef` above is what the BUILDER shows and is written on every keystroke. Every request used
    // to read it, so a rule typed but never applied reached the server on the next page change, the
    // next page-size change, the next date pick — and the export — while the grid on screen had been
    // produced without it. The request now reads THIS copy, which moves only on "החל מסננים", on a
    // chip delete and on "נקה הכל". Same ref + state pair as `advRef`, for the same reason: the
    // chips commit and search in one handler, so the request must not be one render behind.
    const appliedRef = useRef<{ rules: SendSearchFilterRule[]; sort: SendSearchSort }>({
        rules: [], sort: defaultSendSearchSort(),
    });
    const [applied, setApplied] = useState<{ rules: SendSearchFilterRule[]; sort: SendSearchSort }>(appliedRef.current);
    const setAppliedAdv = (next: { rules: SendSearchFilterRule[]; sort: SendSearchSort }) => {
        appliedRef.current = next;
        setApplied(next);
    };

    // The wire request. The advanced parts are appended to the frozen §3.2 body; the cast is on the
    // OBJECT and not on the individual fields so that the base projection stays type-checked, and it
    // exists only because `SendSearchRequest` is B4's to extend. Incomplete rules are dropped by
    // `completeRules` — never sent with a blank value, which a CONTAINS would read as "match all".
    // `FilterGroupID` is NOT sent: CONTRACT §2 says the SERVER assigns it by order.
    // 11-sendsearch §8: from the APPLIED copy, never from the builder (see `appliedRef`).
    const buildRequest = () => {
        const rules = completeRules(appliedRef.current.rules, searchableFields);
        const sort = appliedRef.current.sort;
        return {
            ...toSendSearchRequest(filters),
            Filters: rules.map((r) => ({
                FieldKey: r.FieldKey,
                Operator: r.Operator,
                Value1: r.Value1,
                // Value2 is BETWEEN-only; '' would bind an empty nvarchar where the TVP wants NULL.
                Value2: (r.Value2 || '').trim() === '' ? null : r.Value2,
            })),
            // FROZEN WIRE NAMES (ORCHESTRATOR AMENDMENT 2026-08-06): `SortField` / `SortDescending`,
            // matching the C# DTO member-for-member. This object is cast `as any` below, so tsc CANNOT
            // catch a misspelling here — and Newtonsoft drops an unknown key without a word, which is
            // exactly how the header showed a sort the grid was not performing.
            SortField: sort.FieldKey ? sort.FieldKey : null,
            SortDescending: sort.Dir === 'desc',
        } as any;
    };

    // 🔴 THE BODY THAT ACTUALLY RAN. Added after review (2026-08-09).
    //
    // `buildRequest()` reads `advRef.current`, which the advanced panel writes on EVERY edit
    // (AdvancedFilterBuilder.tsx:84 updateRule, :109 addRule, :112 removeRule, :238 clear) — and none
    // of those dispatch a search, because a rule is applied by "החל מסננים" and not per keystroke
    // (see the effect below). So between an edit and an Apply, `buildRequest()` describes a query the
    // GRID HAS NEVER RUN. The export used to call it fresh at click time, which meant the file could
    // answer a different question from the screen that produced it, while the dialog printed the row
    // count of the OLD search — over-reporting in the direction that matters most: the operator
    // confirms "ייוצאו 4,312 שורות" and files a 118-row audit answer. The mirror case is worse in the
    // other direction: blanking a rule's value drops it at the wire boundary, so the file comes back
    // WIDER than the screen.
    //
    // Every dispatch goes through `runSearch` and records its body here, so the export posts the body
    // that produced the rows on screen, by construction rather than by discipline. This ref is the
    // single reason `SendSearchExportDialog`'s criteria table, its row count and the file can be
    // relied on to describe one search.
    // (11-sendsearch §8: `buildRequest()` now reads the APPLIED copy, which closed the same gap for
    // paging and page size too. This ref stays — it is still the one body the grid actually ran.)
    const lastSentRef = useRef<SendSearchRequest | null>(null);
    // Which applied rules the last search actually carried (by rule Id) — see the catalog-drift
    // effect below. null until the first search.
    const lastRulesKeyRef = useRef<string | null>(null);
    const runSearch = () => {
        const body = buildRequest();
        lastSentRef.current = body;
        lastRulesKeyRef.current = rulesKeyOf(completeRules(appliedRef.current.rules, searchableFields));
        dispatch(searchSends(body));
    };

    // 11-sendsearch §8 bug fix — every re-search starts at PAGE 0. "Apply", a chip delete and "חפש"
    // on an unchanged term used to re-run the CURRENT page: page 4 of a result that the new rule
    // narrowed to 30 rows is empty, and an empty grid is how this screen says "no match".
    // On any other page the page reset IS the refetch (the `filters` effect below fires once);
    // already on page 0 the filters object does not change, so the search is run directly. Never
    // both — that is the double POST /Search this screen has been fixed for twice already.
    const restartSearch = () => {
        if (filters.PageIndex !== 0) dispatch(setPageIndex(0));
        else runSearch();
    };

    // "החל מסננים": the builder's rules + sort become the applied set, then search from page 0.
    const applyAdvanced = () => {
        setAppliedAdv({ rules: advRef.current.rules, sort: advRef.current.sort });
        restartSearch();
    };

    // A chip delete drops that ONE rule from both copies — the builder must not keep offering a rule
    // the operator just removed, and any OTHER unapplied edit in the builder stays unapplied.
    const removeAppliedRule = (id: string) => {
        setRules(advRef.current.rules.filter((x) => x.Id !== id));
        setAppliedAdv({ ...appliedRef.current, rules: appliedRef.current.rules.filter((x) => x.Id !== id) });
        restartSearch();
    };
    const clearAppliedSort = () => {
        setSort(defaultSendSearchSort());
        setAppliedAdv({ ...appliedRef.current, sort: defaultSendSearchSort() });
        restartSearch();
    };

    // "נקה הכל" clears the ADVANCED rules and the sort too — both copies. Leaving them behind would
    // make a cleared screen still silently narrowed and still silently reordered, under a button
    // that just told the user everything was cleared. `clearFilters` builds a new filters object, so
    // the effect below runs the one search.
    const clearAll = () => {
        setRules([]);
        setSort(defaultSendSearchSort());
        setAppliedAdv({ rules: [], sort: defaultSendSearchSort() });
        dispatch(clearFilters());
    };

    // The grid header for the sort column: the field's DISPLAY name, or null when unsorted (which
    // is what hides the column entirely). The APPLIED sort — the one the rows are actually in.
    const sortFieldLabel: string | null = applied.sort.FieldKey
        ? ((fieldByKey(searchableFields, applied.sort.FieldKey)?.DisplayName) || applied.sort.FieldKey)
        : null;

    // One fetch per committed filter object. The free-text box is local state inside the filter bar
    // until "חפש"/Enter commits it, so typing never fires a request; every OTHER control commits
    // immediately, which is what the mock does (its selects call applyF() directly).
    // NOTE the dependency list: `filters` ONLY. The advanced rules are deliberately NOT here — a
    // rule is applied by "החל מסננים" (or by deleting its chip), not on every keystroke inside a
    // value box, which would fire a search per character and show the user results for a prefix of
    // the number they are typing.
    useEffect(() => {
        runSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, filters]);

    // Review round 2 — the grid must run with the rules its chips show. The applied rules that reach
    // the wire depend on the CATALOG: `completeRules` drops a rule whose field is not in the current
    // field list. A channel (or date) change searches at once with the OLD list and swaps the catalog
    // afterwards, so the chips (new list) and the grid (old list) could disagree about a rule. When the
    // set that would be sent now differs from the set the last search sent, search again (page 0).
    // Declared AFTER the `filters` effect on purpose: on Apply / chip delete / clear-all that effect
    // (or the handler itself) has already recorded the new set, so this one sees no difference and
    // never adds a second POST /Search.
    const effectiveRulesKey = rulesKeyOf(completeRules(applied.rules, searchableFields));
    useEffect(() => {
        if (lastRulesKeyRef.current === null || lastRulesKeyRef.current === effectiveRulesKey) return;
        restartSearch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveRulesKey]);

    // 🔴 ADDED 2026-08-08 (review R1-03). `getSendSearchFilterFields` was defined, exported and had
    // all three extraReducer cases wired (sendSearchSlice.ts:155, :443, :455, :474) — and NOTHING in
    // the entire tree ever dispatched it. `GET api/SendSearch/FilterFields` was therefore never
    // issued by the browser (confirmed on staging: the request is absent from the Network tab, not
    // failing — absent), so `filterFields` stayed `[]` for the whole session.
    //
    // This is the SECOND of two stacked breaks: repointing the selector at `filterFields` (above)
    // fixes nothing on its own, because the array it now reads was never populated.
    //
    // 🔴 RE-KEYED 2026-08-09 (multi-campaign). This was keyed on the CHANNEL alone, on the argument
    // that the catalog "cannot change" within a channel. That argument was wrong, and the campaign
    // picker is what made it visible.
    //
    // The catalog SP takes @prm_DateFrom / @prm_DateTo / @prm_IncludeOverOneYear and scopes its
    // #Camp by them. A channel-only call therefore always describes the SP's default window — the
    // last twelve months — no matter what dates are on screen. Two consequences:
    //   • the campaign picker would offer only the last year's campaigns, so ticking "כלול חיפוש
    //     שליחות מעל שנה" would fill the GRID with older sends the PICKER could not name;
    //   • the field list had the same defect already, quietly: the columns offered were the
    //     last-12-months set even when the user had chosen a narrower or wider range.
    //
    // The cost is one request per committed date change. Dates are COMMITTED filters, not
    // keystrokes — the same standard that lets them refetch the grid — so this is one request per
    // deliberate user action, not per character. The slice's `filterFieldsReqId` stale-guard already
    // orders the responses, which is what makes a chattier key safe.
    //
    // NOT keyed on the campaign selection, and that is the one exclusion that matters: the picker's
    // options must never be narrowed by the picker's own selection. That is precisely the
    // self-narrowing failure the deleted client-side catalog had.
    //
    // 11-sendsearch review 2026-09-30: the catalog is asked for channel 1 until the SEARCH has said
    // the server is two-channel ready. An API that predates the change answers FilterFields?channel=0
    // with 400, which would empty the picker and "עוד מסננים" for every tenant if this React shipped
    // first. Review round 2: `channelsReady` is null until the first search settles, and the effect
    // WAITS for it — one catalog call per page load, with the right channel, in both modes.
    const channelsKnown = sendSearch.channelsReady !== null && sendSearch.channelsReady !== undefined;
    const catalogChannel = sendSearch.channelsReady === true ? filters.Channel : eSendSearchChannel.EMAIL;
    useEffect(() => {
        if (!channelsKnown) return;
        dispatch(getSendSearchFilterFields({
            channel: catalogChannel,
            dateFrom: filters.DateFrom,
            dateTo: filters.DateTo,
            includeOverOneYear: filters.IncludeOverOneYear,
        }));
    }, [dispatch, channelsKnown, catalogChannel, filters.DateFrom, filters.DateTo, filters.IncludeOverOneYear]);

    // ── campaign picker options ───────────────────────────────────────────────────────────────
    // 🔴 REPLACED 2026-08-09 (multi-campaign). This was ~70 lines of client-side accumulation: the
    // options were the distinct campaigns found in RESULT ROWS, merged into a growing catalog, with
    // a second effect resetting it whenever the search scope changed. All of it is deleted.
    //
    // It existed only because §3.3 defined no campaign-list endpoint. There is one now — result set
    // [1] of dbo.DataSources_SearchSendsFilterCatalog (51-CatalogSP-Campaigns.sql), built from the
    // SAME #Camp set the field catalog uses, so every option is a campaign the grid can genuinely
    // return rows for.
    //
    // The deleted code had two documented completeness ceilings that no amount of client cleverness
    // could close: a campaign living only on an unvisited PAGE was unknown, and changing scope while
    // filtered to one campaign could only ever reveal that one campaign. Both were survivable for a
    // single-select. Neither survives a SEARCH BOX: the user types a campaign name, gets "not
    // found", and concludes the campaign does not exist — when the truth is that its page was never
    // fetched. That is a confident falsehood on an audit screen, which is the one thing this screen
    // is built not to produce. Deleting the accumulation is what makes the search box honest.
    //
    // Kept deliberately as a plain read with no fallback to the old behaviour: a half-list that
    // LOOKS complete is worse than an empty picker that says it could not load. `campaignsError`
    // carries that distinction to the bar.
    const campaigns: CampaignOption[] = sendSearch.campaigns ?? [];

    const hasFilter = !!filters.SearchText
        || filters.RoleFilter !== eRoleFilter.All
        || filters.RowKind !== eRowKind.All
        || filters.CampaignID != null
        // The multi-select is a filter like any other: without this, narrowing to a campaign that
        // returns nothing would show the "no results" state WITHOUT the "נקה הכל" way out, and the
        // user would be stuck looking at an empty grid with no visible cause.
        || (filters.CampaignIDs != null && filters.CampaignIDs.length > 0)
        || filters.DateFrom != null
        || filters.DateTo != null
        // 11-sendsearch §8: the channel segment is a filter now, and "נקה הכל" resets it.
        || filters.Channel !== eSendSearchChannel.ALL
        // An advanced rule is a filter like any other: without this, a search narrowed to nothing by
        // an advanced rule showed the "no results" state WITHOUT the "נקה הכל" way out, and the user
        // had to guess that the empty grid was caused by the panel they had collapsed.
        // The APPLIED rules — an unapplied one narrows nothing.
        || completeRules(applied.rules, searchableFields).length > 0;

    // ── export (POST api/SendSearch/Export) ───────────────────────────────────────────────────
    // HIDDEN, not disabled, for a sub-user who may not export. That is the house convention and the
    // reason is not cosmetic: `DataSources.tsx:110-114` derives the identical pair, and
    // `DownloadFiles.tsx:67` simply omits the whole download column rather than greying it out.
    // A greyed-out button with an explanatory tooltip would tell a deliberately restricted user
    // exactly which capability was withheld from them — a permission denial explained to the person
    // it is aimed at. The server enforces the same rule anyway (405 USER_PERMISSION_NOT_ALLOWED),
    // so this is the UI half of a gate that exists on both sides.
    //
    // HideRecipients is part of the condition and not an afterthought: three of the 14 export
    // columns are recipient data — name, e-mail, mobile — so a user who may not SEE recipients on
    // screen must certainly not be able to write them to a file and take them home. (The 2026-08-23
    // narrowing dropped ClientID, which does NOT weaken the case for this gate: name/e-mail/mobile
    // are the identifying fields and all three survive.)
    // Same composition DataSources.tsx:114 uses.
    //
    // 🔴 ADDED after review (2026-08-09) — LOCK_EXPORT_DATA. The two sub-user permissions above are
    // only half the gate this product actually enforces. Account feature 13 (LOCK_EXPORT_DATA,
    // `Dal/Models/AccountFeatures/AccountFeatures.cs:17`) is the switch an account buys to stop
    // recipient data leaving the product at all, and EVERY other export in this app honours it:
    // ClientSearch, Groups (:460), DynamicGroups, the newsletter archive, DirectSendReport,
    // SmsReplies, WhatsappInbound, MmsReport, NewslettersReport, ProductsReport, SmsReport and
    // LandingPages — twelve screens, all with the identical `indexOf(...) === -1` composition copied
    // here. Omitting it made THIS screen the way around it, on the most recipient-revealing dataset
    // in the product: name + email + mobile + supervisor address for up to 20,000 people.
    //
    // Client-side only, and that is not an oversight — it is where this feature is enforced. A
    // repo-wide grep of WebSiteApiNew for LockExportData returns the enum declaration and NOTHING
    // else: no C# code anywhere checks feature 13. Adding a server gate here would make SendSearch
    // the single endpoint in the product that enforces it, would widen the frozen contract's 405
    // condition (!AllowExport OR HideRecipietns), and belongs to whoever decides it product-wide.
    const canExport = !!userRoles?.AllowExport
        && !userRoles?.HideRecipients
        && accountFeatures?.indexOf(PulseemFeatures.LOCK_EXPORT_DATA) === -1;
    const [exportOpen, setExportOpen] = useState(false);
    // The wire body, CAPTURED at the moment the dialog opens rather than recomputed on every render
    // of the panel behind it. Two reasons, both about the file matching the screen:
    //   • the criteria table the operator confirms and the `Criteria` the server writes into the
    //     file are then provably the same object, not two evaluations of the same function;
    //   • nothing the panel does while the dialog is open can move it. `buildRequest()` reads
    //     `advRef.current`, which is written synchronously by the chips — a recompute mid-dialog
    //     could silently swap the criteria under a table the user is reading.
    const [exportRequest, setExportRequest] = useState<SendSearchRequest | null>(null);
    // The row count, captured WITH the body (review round 3): the catalog-drift effect can re-search
    // while the dialog is open, and a live count would then describe a different search than the one
    // the dialog exports.
    const [exportTotal, setExportTotal] = useState(0);
    // QA BUG-05-r4: the filters, captured WITH the body. The dialog renders its criteria rows from them
    // (every row SendSearch.ts `buildExportCriteria` reads from `f`: the dates, the role, the row kind, the
    // over-one-year window) and posts them as `Criteria` next to the captured body; live filters let a date
    // that settles while the dialog is open (SendSearchFilters' settle timer) relabel a file whose rows were
    // never filtered by it.
    const [exportFilters, setExportFilters] = useState<Filters | null>(null);
    // QA BUG-05-r4, same reason: the two criteria inputs that state how the captured search RAN. A date that
    // commits after «ייצוא» re-fetches the catalog, and a live `incompleteRuleCount` would then print "N rules
    // were not applied" over a body that applied them. The NAME inputs (campaigns, campaignsError, fields)
    // stay live: they only label ids and keys, and a list still loading at open must still resolve them.
    const [exportIncomplete, setExportIncomplete] = useState(0);
    const [exportChannelsReady, setExportChannelsReady] = useState(false);
    // Rules the user built but did not finish. `toSendSearchRequest` drops them, so they narrow
    // nothing — the dialog states that in the criteria block rather than letting the file imply a
    // narrower search than the one that ran.
    // 11-sendsearch §8: counted over the APPLIED set — the export posts the last-sent body, which
    // was built from it; an edit still sitting in the builder is in neither the grid nor the file.
    const incompleteRuleCount = Math.max(applied.rules.length - completeRules(applied.rules, searchableFields).length, 0);
    const exportDisabled = !!sendSearch.loading || (sendSearch.totalCount ?? 0) === 0;

    // ── drawer ────────────────────────────────────────────────────────────────────────────────
    // The stack stores the row's COMPOSITE KEY, never a copy of the row (Models/…/SendSearch.ts
    // DrawerEntry), so a refetch while the drawer is open cannot leave stale data on screen.
    // Resolving it here is that lookup; a level whose row is gone after a refetch renders nothing
    // rather than last-known data.
    //
    // It must be `sendSearchRowKey`, NOT `RowID`. `RowID` is a DataSourceRows key — PK
    // (DataSourceVersionID, RowID) — and a repeat send of the same campaign produces a SECOND report
    // row carrying the same RowID (CONTRACT §9 fixes the report's row key at
    // `(Channel, ChannelCampaignID, RowID, SentAt)` for exactly this reason, and §2.1 leaves the
    // provenance table without a unique constraint for exactly this reason). Matching on RowID
    // returned the FIRST such row, so opening the drawer on a repeat-sent recipient showed a
    // different send than the one clicked — wrong version, wrong timestamp, in the drawer whose
    // whole purpose is to answer "what exactly was sent to this person, when".
    const rowByKey = (rowKey: string): SendSearchRow | null =>
        items.filter((r) => sendSearchRowKey(r) === rowKey)[0] ?? null;

    // GET api/SendSearch/RowValues for ONE recipient. Guarded on ClientID because the field is new
    // (B.2 adds it to dbo.DataSources_SearchSends): against a server that has not been deployed yet
    // the value is `undefined`, and dispatching would send `clientId=undefined` for the API to answer
    // 400. Not dispatching leaves the drawer's own `valuesUnavailable` branch to say "could not
    // load" — which is the truth — instead of the no-values sentence, which would be a claim.
    const fetchRowValues = (row: SendSearchRow) => {
        if (!row.ClientID || row.ClientID <= 0) return;
        // 12-fix-wave 5.12 — RowValues serves an e-mail row only (the API answers 400 for any other
        // channel); a WhatsApp row neither asks nor shows the values card (AgentDrawer).
        // 11-sendsearch §8: "an e-mail row" is now "a person with an e-mail attempt" (`HasEmail`) —
        // in a combined send a WhatsApp-only person sits under Channel 1 with no e-mail to ask about.
        if (!rowHasEmailAttempt(row)) return;
        dispatch(getSendRowValues({
            campaignId: row.ChannelCampaignID,
            clientId: row.ClientID,
            channel: row.Channel,
        }));
    };

    const openRow = (row: SendSearchRow) => {
        // A supervisor / roll-up recipient opens the ROLL-UP level; everyone else opens the agent level.
        const level = row.IsSupervisor ? 'rollup' : 'agent';
        // 2026-10-02 (Idan): the SOURCE row's phone, as the grid prints it — never the client card's.
        // QA BUG-22 / K-19: the e-mail and the phone as PARTS, each with the grid's chip («מוסר» / «שגוי») when
        // its card is removed / invalid TODAY; DrawerStack prints each part LTR-isolated and the chip outside it,
        // with its tooltip. `Subtitle` keeps the plain text (no status word).
        const phone = rowRecipientPhoneOf(row);
        const subtitleParts: DrawerSubtitlePart[] = [];
        if (row.RecipientEmail) subtitleParts.push({ Text: row.RecipientEmail, Of: 'email', Block: rowRecipientEmailBlockOf(row) });
        if (phone.phone) subtitleParts.push({ Text: phone.phone, Of: 'phone', Block: phone.block });
        dispatch(pushDrawer({
            Level: level,
            RowKey: sendSearchRowKey(row),
            RowID: row.RowID,
            Crumb: row.RecipientName,
            Title: row.RecipientName,
            Subtitle: [row.RecipientEmail, phone.phone].filter((v) => !!v).join(' · ') || null,
            SubtitleParts: subtitleParts,
            Channel: null,
        } as DrawerEntry));
        // The per-campaign provenance history backs the drawer's version card. Fetched on open, not
        // with the grid: it is one request per opened row instead of one per page of rows.
        // Review round 2: a person who got ONLY the WhatsApp of a combined send is versioned by the
        // UNIT's channel-3 provenance (the server's ladder for that row), not by the e-mail's.
        const waOnlyOfCombined = row.HasEmail === false && (row.WACampaignID ?? 0) > 0;
        dispatch(getSendProvenance(waOnlyOfCombined
            ? { campaignId: row.WACampaignID as number, channel: eSendChannel.WHATSAPP }
            : { campaignId: row.ChannelCampaignID, channel: row.Channel }));
        // The values card lives in AgentDrawer only, so the roll-up level does not pay for a request
        // it will not render. `fetchRowValues` guards the ClientID.
        if (level === 'agent') fetchRowValues(row);
        // A rollup is a supervisor: fetch that supervisor's OWN send (opens/clicks + sent-HTML),
        // scoped to THIS campaign and narrowed by the supervisor's email. One request per opened
        // rollup. The roster (SupervisorAgents) is fetched by the effect below, once this returns a
        // RequestID — the report row itself carries no RequestID to key the roster on.
        if (level === 'rollup') {
            dispatch(searchSupervisorSends({
                Channel: row.Channel,
                CampaignID: row.ChannelCampaignID,
                SearchText: row.RecipientEmail || null,
                DateFrom: null,
                DateTo: null,
                PageIndex: 0,
                PageSize: DEFAULT_PAGE_SIZE,
            }));
        }
    };

    // Roster row → push the agent level ON TOP of the roll-up (Mock-v3:473 `pushAgent`). The
    // breadcrumb then reads "מפקח … › סוכן", and Esc pops back to the roll-up — not to the grid.
    const openAgentFromRoster = (row: SendSearchRow) => {
        // QA BUG-22: the e-mail as a part, with the grid's chip (column 46) — the same header `openRow` gives the
        // same person. No phone here (K-30, unchanged).
        const subtitleParts: DrawerSubtitlePart[] = row.RecipientEmail
            ? [{ Text: row.RecipientEmail, Of: 'email', Block: rowRecipientEmailBlockOf(row) }]
            : [];
        dispatch(pushDrawer({
            Level: 'agent',
            RowKey: sendSearchRowKey(row),
            RowID: row.RowID,
            Crumb: row.RecipientName,
            Title: row.RecipientName,
            Subtitle: row.RecipientEmail || null,
            SubtitleParts: subtitleParts,
            Channel: null,
        } as DrawerEntry));
        // MANDATORY here, not only in openRow: the roll-up and the agent share a campaign, so the
        // provenance already in the store is still correct — but the values are per PERSON. Without
        // this dispatch the agent pushed from the roster would render the SUPERVISOR's ID numbers
        // under the agent's name, which is a confident lie of exactly the kind this drawer exists
        // to prevent.
        fetchRowValues(row);
    };

    // The recipients a roll-up covered. §3.2 gives a report row only `SupervisorName` — a STRING; there
    // is no supervisor id — so the grouping key is that string, scoped to the same campaign and channel
    // to keep a collision from pulling in another campaign's agents. The drawer states, in a banner,
    // that the roster is reconstructed rather than recorded (Mock-v3:461-463).
    //
    // CHANGED 2026-08-16 (defect D2). The key is the ADDRESS, not the display name.
    // `SupervisorName` is the RAW supervisor-column value from the row's RowJson — for a data-source
    // campaign that is an e-mail. The roll-up row's own identity is `RecipientEmail`. Comparing the
    // value to `RecipientName` therefore compared an address to a display name, never matched, and left
    // `roster` empty — which is why the drawer's three coverage tiles read 0 while the recorded roster
    // card beneath them listed five people.
    //
    // `!r.IsSupervisor` is KEPT ON PURPOSE. It is NOT what excluded a dual-role person: the SP now
    // stamps IsSupervisor = 0 on every CampaignSendingLog row, so someone who is both supervisor and
    // agent is admitted here by his AGENT row. The clause's only remaining job is to keep a supervisor's
    // own roll-up row out of its own roster, and it costs nothing to keep.
    const rosterFor = (rollupRow: SendSearchRow): SendSearchRow[] => {
        const supKey = (rollupRow.RecipientEmail || '').toLowerCase();
        // A roll-up row with no address can match nothing. Without this guard the comparison below
        // would be '' === '' for any row whose SupervisorName is an empty string.
        if (!supKey) return [];
        // `HasEmail !== false` (review 2026-09-30): the supervisor's mail covers the agents who got the
        // E-MAIL. A WhatsApp-only person of a combined send carries the same campaign and supervisor
        // column, and would otherwise sit in the e-mail roster as "לא מזוהה".
        return items.filter((r) => !r.IsSupervisor
            && r.HasEmail !== false
            && r.Channel === rollupRow.Channel
            && r.ChannelCampaignID === rollupRow.ChannelCampaignID
            && !!r.SupervisorName
            && r.SupervisorName.toLowerCase() === supKey);
    };

    // ── supervisor sends (feature) ────────────────────────────────────────────────────────────
    // The supervisor send matching the OPEN rollup, from the campaign-scoped list openRow fetched.
    // Matched on campaign + email, case-insensitively: RecipientEmail (report row) and
    // SupervisorEmail (SupervisorSends row) are the same address projected by two different procs.
    // Null until the fetch returns, and null forever on a server that has not shipped SupervisorSends
    // — in which case RollupDrawer renders exactly as it did before this feature.
    const supervisorSends: SupervisorSendRow[] = sendSearch.supervisorSends ?? [];
    const topEntry: DrawerEntry | null = drawerStack.length > 0 ? drawerStack[drawerStack.length - 1] : null;
    const openRollupRow: SendSearchRow | null = topEntry && topEntry.Level === 'rollup'
        ? rowByKey(topEntry.RowKey) : null;
    const supervisorSendForOpen: SupervisorSendRow | null = openRollupRow
        ? (supervisorSends.filter((s) => s.CampaignID === openRollupRow.ChannelCampaignID
            && (s.SupervisorEmail || '').toLowerCase() === (openRollupRow.RecipientEmail || '').toLowerCase())[0] ?? null)
        : null;

    // The roster keys on RequestID + SupervisorEmail, which ONLY the SupervisorSendRow carries — the
    // report row has no RequestID — so this is a DEPENDENT fetch, dispatched once the supervisor send
    // above resolves. Keyed on the two primitives so it fires once per (request, supervisor), no loop.
    const supReqId = supervisorSendForOpen?.RequestID ?? 0;
    const supEmail = supervisorSendForOpen?.SupervisorEmail ?? '';
    useEffect(() => {
        if (supReqId > 0 && supEmail) {
            dispatch(getSupervisorAgents({ requestId: supReqId, supervisorEmail: supEmail }));
        }
    }, [dispatch, supReqId, supEmail]);

    const renderDrawerBody = () => {
        const top = drawerStack.length > 0 ? drawerStack[drawerStack.length - 1] : null;
        if (!top) return null;
        const row = rowByKey(top.RowKey);
        if (!row) return null;
        if (top.Level === 'rollup') {
            return (
                <RollupDrawer
                    row={row}
                    roster={rosterFor(row)}
                    provenance={sendSearch.provenance ?? []}
                    onOpenAgent={openAgentFromRoster}
                    // The supervisor's own send (opens/clicks + sent-HTML). Null ⇒ the card renders
                    // as before. The recorded roster is passed ONLY once we have a matching supervisor
                    // send, so a pre-feature server keeps the reconstructed roster (no regression).
                    supervisorSend={supervisorSendForOpen}
                    supervisorAgents={supervisorSendForOpen ? (sendSearch.supervisorAgents ?? []) : undefined}
                    supervisorAgentsLoading={!!sendSearch.supervisorAgentsLoading}
                    supervisorAgentsError={sendSearch.supervisorAgentsError ?? null}
                    // 2026-10-02: for the WhatsApp phone in WaSentMessageDialog (the roster's WhatsApp button).
                    appClasses={classes}
                />
            );
        }
        // 'agent' — and 'message' too: V1 stores no per-recipient as-sent snapshot (CONTRACT D5/§9),
        // so nothing pushes a 'message' level and no reconstructed message is ever shown. The stack
        // and the breadcrumb already support the third level for when V2 supplies its content.
        return (
            <AgentDrawer
                row={row}
                provenance={sendSearch.provenance ?? []}
                provenanceLoading={!!sendSearch.provenanceLoading}
                provenanceError={sendSearch.provenanceError ?? null}
                rowValues={sendSearch.rowValues ?? []}
                rowValuesLoading={!!sendSearch.rowValuesLoading}
                rowValuesError={sendSearch.rowValuesError ?? null}
                // WHOSE values are in the store. The slice has ONE slot, and the drawer refuses to
                // render it unless it belongs to the recipient on screen: someone else's ID and
                // policy numbers under this person's name are indistinguishable, on screen, from
                // the truth. A mismatch also covers "the fetch was never dispatched", which the
                // drawer must report as unknown rather than as "nothing was sent".
                rowValuesClientId={sendSearch.rowValuesClientId ?? null}
                // 11-sendsearch §8: for the WhatsApp phone in WaSentMessageDialog (see `classes`).
                appClasses={classes}
            />
        );
    };

    return (
        <>
            {showTitle && (
                <Typography component="h1" style={{ fontSize: 22, fontWeight: 800, margin: '0 0 16px' }}>
                    {t(`${SS}title`)}
                </Typography>
            )}

            {/* A failed load must not leave the previous rows on screen pretending to be the answer —
                the slice clears `items` on failure. 11-sendsearch §8: WHY the grid is empty is now
                said IN the grid (`error` → SendSearchTable's error state), in place of the "no
                results" sentence it used to fall through to; the banner that stood here repeated
                the same sentence twice (title and body) above the filters. */}

            <SendSearchFiltersBar
                value={filters}
                campaigns={campaigns}
                // Lets the picker say "could not load the campaign list" instead of rendering an
                // empty menu, which would assert — from a failed request — that this account has no
                // campaigns. Also covers the pre-51 server, where the field list arrives fine and
                // only the campaign result set is absent.
                campaignsError={sendSearch.campaignsError ?? null}
                // The catalog waits for the first search (see `channelsKnown`); until it arrives the
                // picker says "loading", not "no campaign found" (review round 3).
                campaignsLoading={!channelsKnown || !!sendSearch.filterFieldsLoading}
                // The source map (script 54). Three separate props rather than one object, so the
                // availability flag can never be inferred from the lists: the flag is a DEPLOYMENT
                // fact and the lists are a DATA fact, and the filter bar has to tell them apart
                // before it renders a control that acts on the operator's behalf.
                sourceMapAvailable={sendSearch.sourceMapAvailable === true}
                sourceOptions={sendSearch.sourceOptions ?? []}
                sourceCampaigns={sendSearch.sourceCampaigns ?? []}
                loading={!!sendSearch.loading}
                onChange={(patch: Partial<Filters>) => dispatch(setFilters(patch))}
                // The filter bar's "חפש" commits the text through onChange; the effect above already
                // refetches on any committed change, so onSearch only needs to force a refetch for the
                // case where the text did NOT change (the user pressing חפש again on the same term).
                // 11-sendsearch §8: from page 0, with the APPLIED advanced set — "חפש" is the text
                // box's commit; the rules have their own ("החל מסננים").
                onSearch={restartSearch}
                onClearAll={clearAll}
                fields={searchableFields}
                rules={advRules}
                onRulesChange={setRules}
                sort={advSort}
                onSortChange={setSort}
                channelsReady={sendSearch.channelsReady === true}
                appliedRules={applied.rules}
                appliedSort={applied.sort}
                onApply={applyAdvanced}
                onRemoveRule={removeAppliedRule}
                onClearSort={clearAppliedSort}
            />

            <Box style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '15px 0 9px', flexWrap: 'wrap' }}>
                <Typography component="span" style={{ fontSize: 16.5, fontWeight: 700 }}>
                    {/* A COUNT, never a percentage. */}
                    {t(`${SS}results.count`, { count: sendSearch.totalCount ?? 0 })}
                </Typography>
                {/* ייצוא. Rendered only for a user who is allowed to export (see `canExport` above —
                    hidden, never greyed, for a restricted sub-user).
                    The Tooltip wraps a <span> and not the Button, for the reason spelled out at
                    SendSearchFilters.tsx:303-305: MUI attaches its listeners to the child, a disabled
                    button fires no pointer events, and the tooltip that explains WHY it is disabled
                    would be the one tooltip that never appears. */}
                {canExport && (
                    <Tooltip
                        title={(exportDisabled
                            ? t(sendSearch.loading ? `${SS}export.disabledLoading` : `${SS}export.disabledNoRows`)
                            : t(`${SS}export.tooltip`)) as string}
                    >
                        <span style={{ marginInlineStart: 'auto' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={exportDisabled}
                                // The LAST DISPATCHED body, never a fresh buildRequest() — see
                                // `lastSentRef` above. There is deliberately no fallback to
                                // buildRequest() here: a fallback would silently restore the exact
                                // divergence the ref exists to remove. It cannot be null in practice
                                // anyway — `exportDisabled` requires totalCount > 0, which requires a
                                // search to have completed.
                                onClick={() => {
                                    const body = lastSentRef.current;
                                    if (!body) return;
                                    setExportRequest(body);
                                    setExportTotal(sendSearch.totalCount ?? 0);
                                    setExportFilters(filters);
                                    setExportIncomplete(incompleteRuleCount);
                                    setExportChannelsReady(sendSearch.channelsReady === true);
                                    setExportOpen(true);
                                }}
                            >
                                {t(`${SS}export.button`)}
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Box>

            <SendSearchTable
                items={items}
                totalCount={sendSearch.totalCount ?? 0}
                pageIndex={filters.PageIndex}
                pageSize={filters.PageSize}
                loading={!!sendSearch.loading}
                hasFilter={hasFilter}
                onOpenRow={openRow}
                onPageChange={(p: number) => dispatch(setPageIndex(p))}
                onPageSizeChange={(s: number) => dispatch(setPageSize(s))}
                onClearAll={clearAll}
                // 11-sendsearch §8: an error is its own state, and the empty state under a channel
                // filter names the channel and offers the way back to both.
                error={sendSearch.error ?? null}
                channel={filters.Channel}
                onShowAllChannels={() => dispatch(setFilters({ Channel: eSendSearchChannel.ALL }))}
                // Null unless a sort field is chosen — that is what keeps the extra column out of
                // the grid entirely in the default case.
                sortFieldLabel={sortFieldLabel}
                // The source map for THIS result set, and the flag that decides whether the grid
                // renders a source line at all. Passed as a pair on purpose: the list alone cannot
                // distinguish "the server has not shipped 52_" from "this result references no
                // source", and rendering the first as the second would put a claim about the data
                // on screen that came from a fact about the deployment.
                sources={sendSearch.sources ?? []}
                sourcesAvailable={!!sendSearch.sourcesAvailable}
            />

            {/* The two standing caveats (`Mock-v3:209-212`). They are page furniture, not a tooltip:
                every number above them is qualified by them — email open tracking is image-load based
                (absence of a record is not proof the mail was not read), email has no delivery receipt,
                SMS has no open metric, and one row = one person as identified by the source row. */}
            <Typography
                component="p"
                style={{ fontSize: 12.5, color: 'var(--pls-d-text-2, #5b6b7b)', maxWidth: '78ch', marginTop: 12 }}
            >
                {t(`${SS}footnote.tracking`)}
            </Typography>

            <Loader isOpen={!!sendSearch.loading} />

            <DrawerStack
                stack={drawerStack}
                isRTL={!!isRTL}
                onPop={() => dispatch(popDrawer())}
                onClose={() => dispatch(closeDrawer())}
            >
                {renderDrawerBody()}
            </DrawerStack>

            {/* Mounted once a request has been captured, and left mounted afterwards so closing it
                animates out instead of vanishing. `open` alone drives visibility; the dialog resets
                all of its own state on every open, so a second export never starts inside the
                previous one's result.
                🔴 The reset is not the whole guard, and reading it as the whole guard was a defect.
                Because the component is left MOUNTED, a request still in flight from the previous
                open resolves onto the live dialog and used to write "the file is ready" over the
                criteria of the search now on screen — a different search than the file answers.
                What actually closes that is inside the dialog: an export-session counter bumped on
                every open and re-checked after the await, plus ESC/backdrop being disabled while a
                request is in flight. Do not "simplify" either of them away on the strength of this
                reset. */}
            {canExport && exportRequest && exportFilters && (
                <SendSearchExportDialog
                    open={exportOpen}
                    onClose={() => setExportOpen(false)}
                    filters={exportFilters}
                    request={exportRequest}
                    totalCount={exportTotal}
                    campaigns={campaigns}
                    // Same flag the filter bar gets: it is what lets the criteria row say "the
                    // campaign names could not be loaded" instead of printing bare ids as if that
                    // were the whole truth.
                    campaignsError={sendSearch.campaignsError ?? null}
                    fields={searchableFields}
                    incompleteRuleCount={exportIncomplete}
                    // Not ready ⇒ the server ran Channel 0 as e-mail; the file must say so.
                    channelsReady={exportChannelsReady}
                />
            )}
        </>
    );
};

export default SendSearchPanel;
